import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  alpha,
  useTheme
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import { createPosition } from "../../../../../../api/services/Communication/position";

const AddPositionDialog = ({ open, onClose, onAdd }) => {
  const theme = useTheme();
  const [positionName, setPositionName] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation(createPosition, {
    onSuccess: () => {
      enqueueSnackbar("Position added successfully", { variant: "success" });
      queryClient.invalidateQueries(QK.positions);
      setPositionName("");
      onClose();
      if (onAdd) onAdd();
    },
    onError: (error) => {
      enqueueSnackbar(error.message || "Failed to add position", { variant: "error" });
    },
  });

  const handleAdd = () => {
    if (positionName.trim() === "") {
      enqueueSnackbar("Please enter a position name", { variant: "warning" });
      return;
    }
    mutation.mutate(positionName.trim());
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAdd();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            m: 2,
            width: 'calc(100% - 32px)',
            maxWidth: '500px'
          }
        }
      }}
    >
      {/* White Header */}
      <Box sx={{ 
        px: 3, 
        py: 2, 
        bgcolor: '#ffffff',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Typography variant="h6" fontWeight={600} color="#1A1A2E">
          Add New Position
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#666' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Position Name"
            placeholder="Enter new position"
            value={positionName}
            onChange={(e) => setPositionName(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                fontWeight: 500,
                color: '#64748b',
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        bgcolor: '#fafafa', 
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, 
        gap: 2 
      }}>
        <Button 
          variant="outlined" 
          onClick={onClose}
          sx={{ 
            color: '#666', 
            fontWeight: 500, 
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            borderColor: alpha(theme.palette.divider, 0.3),
            '&:hover': {
              borderColor: '#666',
              bgcolor: alpha(theme.palette.divider, 0.05)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={mutation.isLoading || !positionName.trim()}
          sx={{ 
            px: 4, 
            fontWeight: 600, 
            textTransform: 'none',
            borderRadius: 2,
            bgcolor: '#0f172a',
            '&:hover': { bgcolor: '#1e293b' },
            '&:disabled': {
              bgcolor: alpha('#0f172a', 0.5),
              color: '#fff'
            }
          }}
        >
          {mutation.isLoading ? "Adding..." : "Add Position"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPositionDialog;